import PageBackTitle from "../Component/PageBackTitle";
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./UserForm.css";
import "./EquipmentForm.css";
import "./AmcSchedules.css";
import "../SafetyObservation/safetyObservation.css";
import "../SafetyMeetings/safetyMeetings.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import API_URL from "../../src/Config";
import MeetingFormFields from "../SafetyMeetings/MeetingFormFields";
import {
  fetchUserOptions,
  parseApiError,
} from "../SafetyObservation/observationUtils";
import {
  buildMeetingFormData,
  INITIAL_MEETING_FORM_DATA,
} from "../SafetyMeetings/meetingUtils";

function SafetyMeetingForm({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState(INITIAL_MEETING_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [userOptions, setUserOptions] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [isAttachmentPopupOpen, setIsAttachmentPopupOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isLoading, setIsLoading] = useState(false);

  const selectedAttendees = useMemo(
    () =>
      userOptions.filter((option) =>
        formData.attendee_ids.includes(option.value),
      ),
    [formData.attendee_ids, userOptions],
  );

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const validateForm = () => {
    const validationErrors = {};

    if (!formData.title.trim()) validationErrors.title = "Title is required.";
    if (!formData.date) validationErrors.date = "Meeting date is required.";

    return validationErrors;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setErrors((prevState) => ({
      ...prevState,
      [name]: "",
    }));
  };

  const handleAttendeesChange = (selectedOptions) => {
    const attendeeIds = Array.isArray(selectedOptions)
      ? selectedOptions.map((option) => option.value)
      : [];

    setFormData((prevState) => ({
      ...prevState,
      attendee_ids: attendeeIds,
    }));
    setErrors((prevState) => ({
      ...prevState,
      attendee_ids: "",
    }));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    setAttachments((prevState) => [...prevState, ...selectedFiles]);
    setErrors((prevState) => ({
      ...prevState,
      attachments: "",
    }));
    event.target.value = "";
  };

  const handleDeleteAttachment = (index) => {
    setAttachments((prevState) => prevState.filter((_, i) => i !== index));
  };

  const handleCancelClick = () => {
    navigate("/safety-meetings");
  };

  const handleSaveClick = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setIsLoading(true);
      const authKey = localStorage.getItem("authKey");
      const payload = buildMeetingFormData(formData, attachments);

      const response = await axios.post(`${API_URL}/api/safety/meetings`, payload, {
        headers: {
          Authorization: authKey,
          "Content-Type": "multipart/form-data",
        },
      });

      showSnackbar(response.data?.message || "Meeting created successfully");
      window.setTimeout(() => {
        navigate("/safety-meetings");
      }, 900);
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to create meeting"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const options = await fetchUserOptions();
        setUserOptions(options);
      } catch (error) {
        showSnackbar(parseApiError(error, "Failed to fetch users"), "error");
      }
    };

    loadUsers();
  }, []);

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content meeting-form-wrapper">
          <div className="data-form-header">
            <div className="data-form-header-content">
              <PageBackTitle className="data-form-heading-content-left-side">New Meeting</PageBackTitle>
            </div>
          </div>
          <div className="header-separator"></div>
          <div className="data-form-body">
            <form className="form-detail">
              <MeetingFormFields
                formData={formData}
                errors={errors}
                userOptions={userOptions}
                selectedAttendees={selectedAttendees}
                onInputChange={handleInputChange}
                onAttendeesChange={handleAttendeesChange}
                fileInputRef={fileInputRef}
                attachments={attachments}
                isAttachmentPopupOpen={isAttachmentPopupOpen}
                onUploadClick={handleUploadClick}
                onFileInputChange={handleFileInputChange}
                onToggleAttachmentPopup={() =>
                  setIsAttachmentPopupOpen((prevState) => !prevState)
                }
                onCloseAttachmentPopup={() => setIsAttachmentPopupOpen(false)}
                onDeleteAttachment={handleDeleteAttachment}
              />
            </form>
          </div>
          <div className="footer">
            <div className="button-group">
              <button
                className="data-form-save-button"
                type="button"
                onClick={handleSaveClick}
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Save"}
              </button>
              <button
                className="data-form-cancel-button"
                type="button"
                onClick={handleCancelClick}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default SafetyMeetingForm;
